"use client";

import * as React from "react";
import { useMedia } from "react-use";
import type { Locale } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ResponsiveDatePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selected?: Date;
  onSelect: (date: Date | undefined) => void;
  disabled?: (date: Date) => boolean;
  title?: string;
  trigger: React.ReactNode;
  fromDate?: Date;
  locale?: Locale;
}

export function ResponsiveDatePicker({
  open,
  onOpenChange,
  selected,
  onSelect,
  disabled,
  title = "날짜 선택",
  trigger,
  fromDate,
  locale,
}: ResponsiveDatePickerProps) {
  const isMobile = useMedia("(max-width: 639px)", false);

  const handleSelect = (date: Date | undefined) => {
    onSelect(date);
    onOpenChange(false);
  };

  // 모바일에서는 캘린더 크기를 키워서 가독성 향상
  const calendarContent = (
    <Calendar
      mode="single"
      selected={selected}
      onSelect={handleSelect}
      disabled={disabled}
      fromDate={fromDate}
      locale={locale}
      initialFocus
      className={
        isMobile
          ? "[--cell-size:--spacing(11)] [&_.rdp-weekday]:text-base [&_.rdp-week]:mt-3 [&_.rdp-day_button]:text-base [&_.rdp-caption_label]:text-base"
          : undefined
      }
    />
  );

  if (isMobile) {
    return (
      <>
        <div onClick={() => onOpenChange(true)}>{trigger}</div>
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="h-auto max-h-[60vh]">
            <DrawerHeader>
              <DrawerTitle>{title}</DrawerTitle>
            </DrawerHeader>
            <div className="flex justify-center pb-6">
              {calendarContent}
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {calendarContent}
      </PopoverContent>
    </Popover>
  );
}
